import { useEffect, useMemo, useState } from 'react';
import { collection, collectionGroup, doc, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { mapDocs } from '@/lib/firestoreHelpers';
import { logger } from '@/lib/logger';
import { type UserProfile } from '@/types/team';
import { mergeCrossOrgMembers, type Membership } from '@/lib/memberships';

interface UseOrgUsersOptions {
    /**
     * When true, returns inactive (removed) users alongside active ones. Defaults
     * to false — most callers want active-only behavior (pickers, rosters, etc).
     * Set true for audit views like the "Show removed members" toggle on the
     * Roster tab.
     */
    includeInactive?: boolean;
}

/**
 * Subscribes to all users in the current org via a single real-time listener.
 * Filters `status === 'inactive'` client-side by default so we don't need a
 * composite index; pass `{ includeInactive: true }` for audit flows.
 *
 * Multi-org: also lists members whose active org is elsewhere, from the
 * memberships collection group (src/lib/memberships.ts). That listener needs
 * the collection-group index on memberships.orgId; if it fails, the roster
 * still shows the users working in this org and the failure is logged.
 */
export function useOrgUsers(options: UseOrgUsersOptions = {}): { users: UserProfile[]; loading: boolean; error: string | null } {
    const { includeInactive = false } = options;
    const { profile } = useAuth();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [sameOrg, setSameOrg] = useState<UserProfile[]>([]);
    const [memberships, setMemberships] = useState<Membership[]>([]);
    const [crossProfiles, setCrossProfiles] = useState<Record<string, UserProfile | undefined>>({});

    useEffect(() => {
        if (!profile?.orgId) {
            setLoading(false);
            return;
        }

        setError(null);

        const q = query(
            collection(db, 'users'),
            where('orgId', '==', profile.orgId),
        );

        const unsubscribe = onSnapshot(q, (snap) => {
            setSameOrg(mapDocs<UserProfile>(snap, 'uid'));
            setLoading(false);
        }, (listenerError) => {
            logger.error('Org users listener failed:', listenerError);
            setError(listenerError.message);
            setLoading(false);
        });

        const unsubscribeMemberships = onSnapshot(
            query(collectionGroup(db, 'memberships'), where('orgId', '==', profile.orgId)),
            (snap) => setMemberships(snap.docs.map((d) => d.data() as Membership)),
            (listenerError) => {
                logger.error('Org memberships listener failed (cross-org members hidden):', listenerError);
                setMemberships([]);
            },
        );

        return () => {
            unsubscribe();
            unsubscribeMemberships();
        };
    }, [profile?.orgId]);

    // Profiles of members working in another org: one listener each.
    const crossUids = memberships
        .map((m) => m.uid)
        .filter((uid) => !sameOrg.some((u) => u.uid === uid))
        .sort()
        .join(',');
    useEffect(() => {
        // Entries left from an earlier member set are harmless: the merge
        // reads only uids that still hold a membership here.
        if (!crossUids) return;
        const unsubs = crossUids.split(',').map((uid) => onSnapshot(
            doc(db, 'users', uid),
            (snap) => setCrossProfiles((prev) => ({ ...prev, [uid]: snap.exists() ? ({ ...snap.data(), uid } as UserProfile) : undefined })),
            (listenerError) => logger.error('Cross-org member profile listener failed:', listenerError),
        ));
        return () => unsubs.forEach((u) => u());
    }, [crossUids]);

    const activeOrgId = profile?.orgId;
    const users = useMemo(() => {
        if (!activeOrgId) return [];
        const all = mergeCrossOrgMembers(sameOrg, memberships, crossProfiles, activeOrgId);
        return includeInactive ? all : all.filter(u => u.status !== 'inactive');
    }, [sameOrg, memberships, crossProfiles, activeOrgId, includeInactive]);

    return { users, loading, error };
}
